import React, { useEffect, useRef, useState } from "react";

/**
 * Lightweight animated counter (no external dep).
 * Animates from 0 to `end` when scrolled into view.
 */
export default function CountUp({ end = 100, duration = 1800, prefix = "", suffix = "", decimals = 0, useSeparator = true, className = "" }) {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) setStarted(true);
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(end * eased);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, end, duration]);

  const formatted =
    decimals > 0
      ? value.toFixed(decimals)
      : useSeparator
      ? Math.floor(value).toLocaleString()
      : String(Math.floor(value));

  return (
    <span ref={ref} className={className} data-testid="count-up">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
